import Grid from "@mui/material/Grid2";
import { FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { DemurrageTerms, DemurrageCountingType } from "__generated__/graphql";
import { DataFormat } from "utils";
import { Fragment, ReactNode } from "react";
import { FormattedDate } from "utils/DateUtils";
import { CalculationMethodDetailsByTypeProps } from "@schemas/calculationmethods/schema";

type DetailsField = {
	id: string;
	render: () => ReactNode;
}

const DemurrageCalculationMethodDetails = ({ calculationMethod, currency, session, hiddenFields }: CalculationMethodDetailsByTypeProps) => {
	const DemurrageCalculationMethodDetailsFields: DetailsField[] = [
		{
			id: "calculationMethod.demurrageTerms",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_TERMS" })} overflow="hidden">
						{intl.formatMessage({ id: `DEMURRAGE_TERMS_${calculationMethod.demurrageTerms}` })}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.demurragePayableOver",
			render: () => calculationMethod.demurrageTerms === DemurrageTerms.Ttb2010 && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_PAYABLE_OVER" })} overflow="hidden">
						{intl.formatMessage({ id: `DEMURRAGE_PAYABLE_OVER_${calculationMethod.demurragePayableOver}` })}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.price",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${calculationMethod.timeUnit}` })} overflow="hidden">
						{DataFormat.price(calculationMethod.price, currency.isoCode, session.locale)}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.pricePerCargoQuantityUnit",
			render: () => calculationMethod.pricePerCargoQuantityUnit && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${calculationMethod.timeUnit}_PER_UNIT` })} overflow="hidden">
						{DataFormat.price(calculationMethod.pricePerCargoQuantityUnit, currency.isoCode, session.locale)}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeAllowed",
			render: () => calculationMethod.demurrageTerms === DemurrageTerms.PerContract && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "LAYTIME_ALLOWED" })} overflow="hidden">
						{`${calculationMethod.laytimeAllowed} ${intl.formatMessage({ id: "HOURS" })}`}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.startCountingTime",
			render: () => calculationMethod.demurrageCountingType === DemurrageCountingType.StartCountingTime && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: `DEMURRAGE_COUNTING_TYPE_${calculationMethod.demurrageCountingType}` })} overflow="hidden">
						<FormattedDate date={calculationMethod.startCountingTime} withoutYear />
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.workingHoursSinceNomination",
			render: () => calculationMethod.demurrageCountingType === DemurrageCountingType.WorkingHoursSinceNomination && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: `DEMURRAGE_COUNTING_TYPE_${calculationMethod.demurrageCountingType}` })} overflow="hidden">
						{calculationMethod.workingHoursSinceNomination}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.workingDaysSinceNomination",
			render: () => calculationMethod.demurrageCountingType === DemurrageCountingType.WorkingDaysSinceNomination && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: `DEMURRAGE_COUNTING_TYPE_${calculationMethod.demurrageCountingType}` })} overflow="hidden">
						{calculationMethod.workingDaysSinceNomination}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.workingDaysSinceNomination",
			render: () => calculationMethod.demurrageCountingType === DemurrageCountingType.ShortNotice && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: `DEMURRAGE_COUNTING_TYPE_${calculationMethod.demurrageCountingType}` })} overflow="hidden">
						{DataFormat.booleanAsText(calculationMethod.shortNotice)}
					</FormGroup>
				</Grid>
			),
		},
	];

	return (
		<>
			{DemurrageCalculationMethodDetailsFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

export default DemurrageCalculationMethodDetails;
